import { ReactNode, useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { DataTable as PrimeDataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Checkbox } from 'primereact/checkbox';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import { Button } from 'sharedUi/Button';
import { Icon } from './icon';
import SearchInput from './search-input';

// PrimeReact's DataTable is a discriminated union that fights generic spreads.
const Table = PrimeDataTable as unknown as React.ComponentType<any>;

export interface SaasColumn<T = any> {
  field: string;
  header: string;
  sortable?: boolean;
  /** Enable the per-column filter in the ⋮ menu. */
  filter?: boolean;
  filterType?: 'text' | 'numeric' | 'date' | 'dropdown';
  /** Options for filterType 'dropdown' (value is matched with EQUALS). */
  filterOptions?: { label: string; value: any }[];
  /** Whether this column can be hidden via its ⋮ menu (default true). */
  hideable?: boolean;
  /** Initial visibility (default true). */
  defaultVisible?: boolean;
  align?: 'left' | 'center' | 'right';
  style?: React.CSSProperties;
  headerStyle?: React.CSSProperties;
  body?: (row: T, options?: any) => ReactNode;
  className?: string;
}

/** How many skeleton rows to draw while a page loads.
 *
 *  Matched to the page size the caller asked for, capped: a 100-row page would draw
 *  a screenful of grey bars and push the real content off the fold when it arrives,
 *  which is a worse jump than the one the skeleton exists to prevent. */
const SKELETON_ROWS_MAX = 8;

/** A per-column bar width, so the skeleton reads as THIS table rather than as a grey
 *  block. Derived from the column's own role — a status chip is short, a name is
 *  long — because a uniform width is what makes placeholder rows look like a bug.
 *
 *  Deterministic per column: a random width would shimmer differently on every
 *  render while the page is still loading. */
const skeletonWidth = (col: SaasColumn, index: number): string => {
  if (col.field === 'actions') return 'w-20';
  if (col.align === 'right') return 'w-12';
  const field = col.field.toLowerCase();
  if (field.includes('status') || field.includes('state')) return 'w-16';
  if (field.includes('code') || field.includes('sku')) return 'w-24';
  if (field.includes('name') || field.includes('title') || field.includes('description')) {
    return 'w-40';
  }
  if (field.includes('date') || field.includes('at')) return 'w-24';
  // Anything unrecognised alternates, so a run of unknown columns does not come out
  // as one flat band.
  return index % 2 ? 'w-28' : 'w-20';
};

export interface SaasDataTableProps<T = any> {
  /** Stable id used to persist column visibility in localStorage. */
  tableId: string;
  columns: SaasColumn<T>[];
  data: T[];
  loading?: boolean;
  dataKey?: string;
  emptyMessage?: string;

  // Server-side pagination
  first: number;
  rows: number;
  totalRecords: number;
  onPage: (e: any) => void;
  rowsPerPageOptions?: number[];

  // Server-side sort
  sortField?: string;
  sortOrder?: 1 | -1 | null;
  onSort: (e: any) => void;

  // Server-side per-column filters (controlled)
  filters?: Record<string, any>;
  onFilter: (e: any) => void;

  // Global search
  globalSearch?: string;
  onGlobalSearchChange?: (v: string) => void;
  globalSearchPlaceholder?: string;

  // Toolbar slots
  toolbarStart?: ReactNode;
  toolbarEnd?: ReactNode;

  // Optional row expansion (opt-in). When rowExpansionTemplate is set, an expander
  // column is prepended and these are passed through to the underlying DataTable.
  expandedRows?: any;
  onRowToggle?: (e: any) => void;
  rowExpansionTemplate?: (row: T) => ReactNode;

  /** When true (default), the table gets its own scroll area sized to fill the
   *  remaining viewport height: the column header freezes, the body scrolls, and
   *  the paginator stays pinned at the bottom. Set false for a table embedded in a
   *  card/tab that should size to its content instead. */
  fillHeight?: boolean;

  /** When true (default), the table is laid out to EXACTLY fit its container width
   *  (`table-layout: fixed`), so it never scrolls horizontally and every column —
   *  including Actions — stays visible. Narrow columns (actions, status, numbers)
   *  get sensible default widths; the rest share the remaining space and their
   *  content truncates. Set false for a wide, many-column table that genuinely
   *  needs to scroll sideways instead of squeezing every column on screen. */
  fitColumns?: boolean;

  /** Extra classes for each data row, e.g. 'cursor-pointer' on a list whose rows
   *  open something. Not applied to the loading skeleton's placeholder rows. */
  rowClassName?: (row: T) => string;

  /** Called with the row when it is clicked. Clicks on buttons, links and
   *  inputs inside the row (its action icons) are left to them. */
  onRowClick?: (row: T) => void;

  /** When true (default), a leading "S.No" column is prepended with GLOBAL row
   *  numbering (continues across pages: page 2 starts at 11). Set false to hide it. */
  showSerial?: boolean;

  /** When true (default), columns can be drag-resized (expand mode: a column grows
   *  and the table can scroll horizontally). Resized widths persist per `tableId`. */
  resizable?: boolean;

  testId?: string;
}

const STORAGE_PREFIX = 'saas-cols:';
const WIDTHS_PREFIX = 'saas-widths:';

const MATCH_MODES = [
  { label: 'Contains', value: FilterMatchMode.CONTAINS },
  { label: 'Equals', value: FilterMatchMode.EQUALS },
  { label: 'Starts with', value: FilterMatchMode.STARTS_WITH },
  { label: 'Ends with', value: FilterMatchMode.ENDS_WITH },
  { label: 'Not contains', value: FilterMatchMode.NOT_CONTAINS },
  { label: 'Not equals', value: FilterMatchMode.NOT_EQUALS },
];

/** Build the default filter-meta object for the filterable columns. */
export function buildDefaultFilters(columns: SaasColumn[]): Record<string, any> {
  const meta: Record<string, any> = {};
  columns.forEach((c) => {
    if (c.filter) meta[c.field] = { value: null, matchMode: FilterMatchMode.CONTAINS };
  });
  return meta;
}

/**
 * Translate the filter-meta into the compact {field:{value,matchMode}} shape the
 * backend expects (empty values dropped). Returns a JSON string, or undefined.
 */
export function buildColumnFiltersParam(filters?: Record<string, any>): string | undefined {
  if (!filters) return undefined;
  const out: Record<string, { value: any; matchMode: string }> = {};
  Object.entries(filters).forEach(([field, meta]: [string, any]) => {
    const spec = meta?.constraints ? meta.constraints[0] : meta;
    const value = spec?.value;
    if (value === null || value === undefined || value === '') return;
    out[field] = { value, matchMode: spec?.matchMode || FilterMatchMode.CONTAINS };
  });
  return Object.keys(out).length ? JSON.stringify(out) : undefined;
}

/** What a table remembers: which columns are shown, and which it had ever heard of. */
interface StoredColumns {
  visible: string[];
  /** Every field that existed when this was written. */
  known: string[];
}

/**
 * `known` is the whole point of the shape.
 *
 * Only visible fields used to be stored, which made "the user hid this column"
 * and "this column did not exist yet" the same state: absent. So a column added
 * to a table later stayed hidden for ever for anyone who had already used it,
 * while new users saw it — the Project columns were invisible to exactly the
 * people who had been in those grids before.
 *
 * Recording what was known separates the two: a field the saved state has never
 * seen is new, and shows. A field it knew and dropped was hidden on purpose, and
 * stays hidden. The old array form is still read, treated as both lists at once,
 * so nobody's arrangement is lost on the way past.
 */
function readVisible(tableId: string): StoredColumns | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + tableId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return { visible: parsed, known: parsed };
    if (parsed && Array.isArray(parsed.visible) && Array.isArray(parsed.known)) {
      return { visible: parsed.visible, known: parsed.known };
    }
    return null;
  } catch {
    return null;
  }
}

function SaasDataTable<T = any>({
  tableId,
  columns,
  data,
  loading,
  dataKey = '_id',
  emptyMessage = 'No records found',
  first,
  rows,
  totalRecords,
  onPage,
  rowsPerPageOptions = [5, 10, 25, 50],
  sortField,
  sortOrder,
  onSort,
  filters,
  onFilter,
  globalSearch,
  onGlobalSearchChange,
  globalSearchPlaceholder = 'Search...',
  toolbarStart,
  toolbarEnd,
  expandedRows,
  onRowToggle,
  rowExpansionTemplate,
  fillHeight = true,
  fitColumns = true,
  showSerial = true,
  resizable = true,
  rowClassName,
  onRowClick,
  testId,
}: SaasDataTableProps<T>) {
  const menuOp = useRef<OverlayPanel>(null); // per-column ⋮ menu

  // User-dragged column widths, persisted per table (like column visibility). Keyed
  // by field → pixel width; applied back onto each column's style on render.
  const [colWidths, setColWidths] = useState<Record<string, number>>(() => {
    try {
      const raw = localStorage.getItem(WIDTHS_PREFIX + tableId);
      return raw ? (JSON.parse(raw) as Record<string, number>) : {};
    } catch { return {}; }
  });
  const persistWidths = useCallback((next: Record<string, number>) => {
    setColWidths(next);
    try { localStorage.setItem(WIDTHS_PREFIX + tableId, JSON.stringify(next)); } catch { /* ignore */ }
  }, [tableId]);
  const resetWidths = useCallback(() => {
    setColWidths({});
    try { localStorage.removeItem(WIDTHS_PREFIX + tableId); } catch { /* ignore */ }
  }, [tableId]);
  // PrimeReact fires this at the end of a drag with the resized <th> and its new width.
  const onColumnResizeEnd = useCallback((e: any) => {
    const field = e?.column?.props?.field;
    const width = Math.round(e?.element?.offsetWidth ?? 0);
    if (!field || !width) return;
    persistWidths({ ...colWidths, [field]: width });
  }, [colWidths, persistWidths]);

  // The scroll region fills from the table's position down to the bottom of the
  // PAGE's scroll container (not the whole window — that includes the fixed top
  // bar / footer and would push the table past the container, giving a second
  // scrollbar). So the frozen header + pinned paginator sit on-screen and only the
  // table body scrolls. The top depends only on the stable page chrome above it,
  // so this doesn't loop.
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [areaHeight, setAreaHeight] = useState<number>();
  useLayoutEffect(() => {
    if (!fillHeight) { setAreaHeight(undefined); return; }
    const scrollParent = (node: HTMLElement | null): HTMLElement | null => {
      let el = node?.parentElement || null;
      while (el) {
        const oy = getComputedStyle(el).overflowY;
        // The page's scroll container is the nearest ancestor that scrolls
        // vertically — identify it by its overflow, not by whether it happens to
        // be overflowing right now (it may not be until we size the table).
        if (oy === 'auto' || oy === 'scroll' || oy === 'overlay') return el;
        el = el.parentElement;
      }
      return null;
    };
    const measure = () => {
      const el = scrollAreaRef.current;
      if (!el) return;
      const sp = scrollParent(el);
      if (!sp) {
        // No scrolling ancestor (short page): fall back to the viewport.
        const top = el.getBoundingClientRect().top;
        setAreaHeight(Math.max(240, Math.round(window.innerHeight - top - 24)));
        return;
      }
      // Size the grid so it ends EXACTLY at the scroll container's content bottom —
      // no second scrollbar. Rather than guess the space below the grid, measure it:
      // the grid is the last child at every level, so the space beneath it is the sum
      // of each ancestor's bottom padding + border up to (and including) the
      // scroll container.
      const offsetTop = el.getBoundingClientRect().top - sp.getBoundingClientRect().top;
      let below = 0;
      let node: HTMLElement | null = el;
      while (node && node !== sp && node.parentElement) {
        const cs = getComputedStyle(node.parentElement);
        below += parseFloat(cs.paddingBottom || '0') + parseFloat(cs.borderBottomWidth || '0');
        node = node.parentElement;
      }
      const SAFETY = 2; // guard against sub-pixel rounding
      setAreaHeight(Math.max(240, Math.round(sp.clientHeight - offsetTop - below - SAFETY)));
    };
    measure();
    window.addEventListener('resize', measure);
    // Re-measure on any layout shift too: sidebar collapse, a filter row wrapping,
    // or async content landing above the grid all move the grid's top.
    const ro = new ResizeObserver(() => measure());
    const sp = scrollParent(scrollAreaRef.current);
    if (sp) ro.observe(sp);
    ro.observe(document.body);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, [fillHeight]);

  const [activeCol, setActiveCol] = useState<SaasColumn<T> | null>(null);
  const [menuView, setMenuView] = useState<'main' | 'filter' | 'columns'>('main');
  const [draftValue, setDraftValue] = useState<string>('');
  const [draftMode, setDraftMode] = useState<string>(FilterMatchMode.CONTAINS);

  /* Only on the FIRST load. A refresh or a page change already has rows on screen,
     and replacing them with grey bars reads as "it broke", not "it is working". */
  const showSkeleton = loading && (data?.length ?? 0) === 0;
  const skeletonRows = useMemo(
    () => Array.from({ length: Math.min(rows || SKELETON_ROWS_MAX, SKELETON_ROWS_MAX) },
                     (_, i) => ({ __skeleton: true, __key: `skeleton-${i}` })),
    [rows],
  );

  const hideableColumns = useMemo(() => columns.filter((c) => c.hideable !== false), [columns]);

  const [visibleFields, setVisibleFields] = useState<Set<string>>(() => {
    const byDefault = columns.filter((c) => c.defaultVisible !== false).map((c) => c.field);
    const saved = readVisible(tableId);
    if (!saved) return new Set(byDefault);
    // Anything the saved state never knew about is new, so it follows the
    // column's own default rather than inheriting somebody's old arrangement.
    const unseen = columns
      .filter((c) => !saved.known.includes(c.field) && c.defaultVisible !== false)
      .map((c) => c.field);
    return new Set([...saved.visible, ...unseen]);
  });

  const persist = useCallback(
    (next: Set<string>) => {
      setVisibleFields(new Set(next));
      try {
        localStorage.setItem(STORAGE_PREFIX + tableId, JSON.stringify({
          visible: Array.from(next),
          known: columns.map((c) => c.field),
        } satisfies StoredColumns));
      } catch {
        /* ignore */
      }
    },
    // `columns` is read for the known-field list, so a table whose column set
    // changes writes the new one rather than a stale snapshot.
    [tableId, columns]
  );

  const toggleField = (field: string, checked: boolean) => {
    const next = new Set(visibleFields);
    if (checked) next.add(field);
    else next.delete(field);
    persist(next);
  };

  const showAll = () => persist(new Set(columns.map((c) => c.field)));
  const hideAll = () => persist(new Set(columns.filter((c) => c.hideable === false).map((c) => c.field)));

  const hasActiveFilter = (field: string) => {
    const meta: any = filters?.[field];
    const spec = meta?.constraints ? meta.constraints[0] : meta;
    return spec?.value !== null && spec?.value !== undefined && spec?.value !== '';
  };

  // ── ⋮ menu actions ──────────────────────────────────────────────
  const openColMenu = (e: React.MouseEvent, col: SaasColumn<T>) => {
    e.stopPropagation();
    const meta: any = filters?.[col.field];
    const spec = meta?.constraints ? meta.constraints[0] : meta;
    setDraftValue(spec?.value ?? '');
    setDraftMode(spec?.matchMode ?? (col.filterType === 'dropdown' ? FilterMatchMode.EQUALS : FilterMatchMode.CONTAINS));
    setActiveCol(col);
    setMenuView('main');
    menuOp.current?.show(e, e.currentTarget as HTMLElement);
  };

  const applyFilter = () => {
    if (!activeCol) return;
    const next = { ...(filters || {}), [activeCol.field]: { value: draftValue === '' ? null : draftValue, matchMode: draftMode } };
    onFilter({ filters: next });
    menuOp.current?.hide();
  };

  const clearFilter = () => {
    if (!activeCol) return;
    const next = { ...(filters || {}), [activeCol.field]: { value: null, matchMode: draftMode } };
    setDraftValue('');
    onFilter({ filters: next });
    menuOp.current?.hide();
  };

  const doSort = (dir: 1 | -1) => {
    if (!activeCol) return;
    onSort({ sortField: activeCol.field, sortOrder: dir });
    menuOp.current?.hide();
  };

  const clearSort = () => {
    onSort({ sortField: null, sortOrder: null });
    menuOp.current?.hide();
  };

  const hideColumn = (field: string) => {
    const next = new Set(visibleFields);
    next.delete(field);
    persist(next);
    menuOp.current?.hide();
  };

  // A non-hideable column is always shown regardless of the toggle set.
  const renderedColumns = columns.filter((c) => c.hideable === false || visibleFields.has(c.field));

  const alignClass = (a?: string) =>
    a === 'center' ? 'text-center' : a === 'right' ? 'text-right' : undefined;

  // In fit mode (table-layout: fixed) columns share the table width. A column with
  // its own `style.width` is honoured; otherwise we pin the obviously-narrow ones
  // (actions, status, numbers, version, dates) to sensible widths and leave the
  // content columns "auto" so they split whatever space is left. This gives every
  // table good proportions without the page having to specify widths.
  const columnStyle = (col: SaasColumn<T>): React.CSSProperties | undefined => {
    // A width the user dragged to wins over everything (persisted per table).
    const persisted = colWidths[col.field];
    if (persisted) return { ...col.style, width: persisted };
    if (!fitColumns || col.style?.width != null) return col.style;
    const f = col.field.toLowerCase();
    let width: number | undefined;
    if (f === 'actions') width = 130;
    else if (f.includes('status') || f.includes('state')) width = 150;
    else if (f === 'version') width = 90;
    else if (col.align === 'right') width = 120;
    else if (f.endsWith('at') || f.includes('date') || f.includes('time')) width = 170;
    return width ? { ...col.style, width } : col.style;
  };

  // Almost every column is sortable; opt out with `sortable: false`. Actions never.
  const isSortable = (col: SaasColumn<T>) => col.sortable !== false && col.field !== 'actions';

  // Direct, click-the-header sorting: asc → desc → off. Drives the same controlled
  // `onSort` the ⋮ menu uses, so server-side sort is unchanged.
  const toggleSort = (col: SaasColumn<T>) => {
    if (!isSortable(col)) return;
    if (sortField !== col.field) { onSort({ sortField: col.field, sortOrder: 1 }); return; }
    if (sortOrder === 1) { onSort({ sortField: col.field, sortOrder: -1 }); return; }
    onSort({ sortField: null, sortOrder: null });
  };

  const renderHeader = (col: SaasColumn<T>) => {
    const sortable = isSortable(col);
    const isSorted = sortField === col.field && !!sortOrder;
    const filtered = hasActiveFilter(col.field);
    const hasMenu = sortable || col.filter || col.hideable !== false;
    const pinned = isSorted || filtered || activeCol?.field === col.field;
    const alignCls = col.align === 'center' ? 'justify-center' : col.align === 'right' ? 'justify-end' : '';
    return (
      <div className={`saas-col-header ${alignCls}`}>
        {sortable ? (
          // The whole label is the sort control — click to sort directly (asc→desc→off),
          // no need to open the ⋮ menu. The ⋮ menu still carries sort too.
          <button
            type="button"
            className="saas-col-sort"
            onClick={() => toggleSort(col)}
            title="Sort"
            aria-label={`Sort by ${col.header}`}
          >
            <span className="saas-col-title">{col.header}</span>
            {isSorted
              ? <Icon name={sortOrder === 1 ? 'sort-asc' : 'sort-desc'} size={13} className="saas-col-ind" />
              : <i className="pi pi-sort-alt saas-col-ind saas-col-ind--idle" />}
          </button>
        ) : (
          <span className="saas-col-title">{col.header}</span>
        )}
        {filtered && <Icon name="filter" size={12} className="saas-col-ind" />}
        {hasMenu && (
          <button
            type="button"
            className={`saas-col-menu-btn ${pinned ? 'is-pinned' : ''}`}
            onClick={(e) => openColMenu(e, col)}
            aria-label={`${col.header} options`}
          >
            <Icon name="more-vertical" size={16} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div data-testid={testId}>
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 flex-wrap px-4 py-3 border-b border-gray-100 dark:border-gray-700/60">
        <div className="flex items-center gap-3 flex-wrap">
          {onGlobalSearchChange && (
            <SearchInput
              value={globalSearch ?? ''}
              onChange={onGlobalSearchChange}
              placeholder={globalSearchPlaceholder}
              testId={testId ? `${testId}.GlobalSearch` : undefined}
            />
          )}
          {toolbarStart}
        </div>

        {/* ml-auto keeps this group pinned to the right edge even when the toolbar
            wraps to a second line (wide filter rows, e.g. Units). */}
        <div className="flex items-center gap-3 flex-shrink-0 ml-auto">{toolbarEnd}</div>
      </div>

      {/* Per-column ⋮ menu (shared, content driven by activeCol + menuView) */}
      <OverlayPanel ref={menuOp} onHide={() => setActiveCol(null)} className="saas-col-menu w-72">
        {activeCol && menuView === 'main' && (
          <div className="flex flex-col gap-1">
            {activeCol.filter && (
              <button type="button" className="saas-menu-item" onClick={() => setMenuView('filter')}>
                <Icon name="filter" size={15} /> Filter
                {hasActiveFilter(activeCol.field) && (
                  <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-indigo-500" />
                )}
                <Icon name="chevron-right" size={13} className="ml-auto opacity-60" />
              </button>
            )}
            {activeCol.filter && isSortable(activeCol) && <div className="saas-menu-divider" />}

            {isSortable(activeCol) && (
              <>
                <button type="button" className="saas-menu-item" onClick={() => doSort(1)}>
                  <Icon name="sort-asc" size={15} /> Sort ascending
                </button>
                <button type="button" className="saas-menu-item" onClick={() => doSort(-1)}>
                  <Icon name="sort-desc" size={15} /> Sort descending
                </button>
                <button type="button" className="saas-menu-item" onClick={clearSort}>
                  <Icon name="x" size={15} /> Clear sort
                </button>
                <div className="saas-menu-divider" />
              </>
            )}

            {activeCol.hideable !== false && (
              <button type="button" className="saas-menu-item" onClick={() => hideColumn(activeCol.field)}>
                <Icon name="eye-off" size={15} /> Hide column
              </button>
            )}
            <button type="button" className="saas-menu-item" onClick={() => setMenuView('columns')}>
              <Icon name="table" size={15} /> Show columns
              <Icon name="chevron-right" size={13} className="ml-auto opacity-60" />
            </button>
          </div>
        )}

        {activeCol && menuView === 'filter' && (
          <div className="flex flex-col">
            <button type="button" className="saas-menu-back" onClick={() => setMenuView('main')}>
              <Icon name="chevron-left" size={15} /> Filter
            </button>
            <div className="flex flex-col gap-2 py-1">
              {activeCol.filterType === 'dropdown' ? (
                <Dropdown
                  value={draftValue === '' ? null : draftValue}
                  options={activeCol.filterOptions || []}
                  onChange={(e) => setDraftValue(e.value ?? '')}
                  placeholder="Select…"
                  showClear
                  className="w-full text-sm"
                />
              ) : (
                <>
                  <Dropdown
                    value={draftMode}
                    options={MATCH_MODES}
                    onChange={(e) => setDraftMode(e.value)}
                    className="w-full text-sm"
                  />
                  <InputText
                    value={draftValue}
                    onChange={(e) => setDraftValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyFilter()}
                    placeholder="Value"
                    className="w-full text-sm"
                    autoFocus
                  />
                </>
              )}
              <div className="flex justify-end gap-2 mt-1">
                <Button label="Clear" severity="secondary" outlined size="small" onClick={clearFilter} />
                <Button label="Apply" size="small" onClick={applyFilter} />
              </div>
            </div>
          </div>
        )}

        {activeCol && menuView === 'columns' && (
          <div className="flex flex-col">
            <button type="button" className="saas-menu-back" onClick={() => setMenuView('main')}>
              <Icon name="chevron-left" size={15} /> Show columns
            </button>
            <div className="flex flex-col gap-0.5 max-h-56 overflow-auto py-1">
              {hideableColumns.map((c) => (
                <label key={c.field} className="saas-col-choice">
                  <Checkbox
                    checked={visibleFields.has(c.field)}
                    onChange={(e) => toggleField(c.field, !!e.checked)}
                  />
                  <span className="flex-1 truncate">{c.header}</span>
                </label>
              ))}
              {hideableColumns.length === 0 && (
                <span className="text-xs text-gray-400 px-2 py-1">No hideable columns</span>
              )}
            </div>
            <div className="saas-menu-divider" />
            <div className="flex flex-col gap-2">
              <Button label="Show all columns" icon="pi pi-check-square" size="small" className="w-full justify-center" onClick={showAll} />
              <Button label="Hide all columns" icon="pi pi-stop" severity="secondary" outlined size="small" className="w-full justify-center" onClick={hideAll} />
              {resizable && Object.keys(colWidths).length > 0 && (
                <Button label="Reset column widths" icon="pi pi-arrows-h" severity="secondary" text size="small" className="w-full justify-center" onClick={() => { resetWidths(); menuOp.current?.hide(); }} />
              )}
            </div>
          </div>
        )}
      </OverlayPanel>

      {/* Skeletons replace an EMPTY table; a reload that already has rows keeps them
          and dims instead. Swapping populated rows for grey bars on every page change
          reads as "it broke", and the reader loses the row they were looking at. */}
      {/* The scroll area: a fixed (measured) height turns the table into its own
          scroll region so the header freezes and the paginator stays pinned. */}
      <div ref={scrollAreaRef}
           className={`${fillHeight ? 'flex min-h-0 flex-col' : ''} ${loading && !showSkeleton ? 'ui-busy' : ''}`.trim() || undefined}
           style={fillHeight && areaHeight ? { height: areaHeight } : undefined}
           aria-busy={loading || undefined}>
      <Table
        value={showSkeleton ? skeletonRows : data}
        /* Not PrimeReact's `loading`: that draws a spinner over an EMPTY table, so
           the header and paginator collapse and then jump back when data lands. Feeding
           it placeholder rows keeps the layout at its final height and tells the reader
           what is coming — the whole point of a skeleton over a spinner. */
        loading={false}
        dataKey={showSkeleton ? '__key' : dataKey}
        emptyMessage={emptyMessage}
        className={`saas-table${fillHeight ? ' saas-table--fill' : ''}${fitColumns ? ' saas-table--fit' : ''}${resizable ? ' saas-table--resizable' : ''}`}
        stripedRows
        rowHover
        paginator
        lazy
        {...(resizable ? { resizableColumns: true, columnResizeMode: 'fit', onColumnResizeEnd } : {})}
        first={first}
        rows={rows}
        totalRecords={totalRecords}
        onPage={onPage}
        rowsPerPageOptions={rowsPerPageOptions}
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        currentPageReportTemplate="Showing {first}–{last} of {totalRecords}  ·  Page {currentPage} of {totalPages}"
        data-testid={testId ? `${testId}.Table` : undefined}
        {...(rowExpansionTemplate && !showSkeleton
          ? { expandedRows, onRowToggle, rowExpansionTemplate }
          : {})}
        {...(rowClassName && !showSkeleton ? { rowClassName } : {})}
        {...(onRowClick && !showSkeleton
          ? {
            onRowClick: (e: { data: unknown; originalEvent: { target: EventTarget | null } }) => {
              const target = e.originalEvent.target as HTMLElement | null;
              if (target?.closest('button, a, input, select, textarea, [role="button"]')) return;
              onRowClick(e.data as T);
            },
          }
          : {})}
      >
        {rowExpansionTemplate && !showSkeleton && (
          <Column expander style={{ width: '3rem' }} />
        )}
        {showSerial && (
          <Column
            header="S.No"
            headerStyle={{ width: '4.5rem' }}
            style={{ width: '4.5rem' }}
            bodyClassName="text-ink-muted tabular-nums"
            body={showSkeleton
              ? () => <span className="ui-skel h-3 w-6 inline-block" />
              // GLOBAL numbering: `first` is the page's starting record index, so
              // page 2 at size 10 starts at 11. `rowIndex` is the page-local index.
              : (_row: T, opt: { rowIndex?: number }) => (first ?? 0) + (opt?.rowIndex ?? 0) + 1}
          />
        )}
        {renderedColumns.map((col, colIndex) => (
          <Column
            key={col.field}
            field={col.field}
            header={renderHeader(col)}
            body={showSkeleton
              ? () => (
                <span className={`ui-skel h-3 ${skeletonWidth(col, colIndex)} ${
                  /* A bar is a block, so it ignores the cell's text-align and sits
                     left — under a right-aligned number the value then jumps across
                     the cell when it lands, which is the jump a skeleton exists to
                     prevent. Pushed to match where the content will actually be. */
                  col.align === 'right' ? 'ml-auto' : col.align === 'center' ? 'mx-auto' : ''
                }`} />
              )
              : (col.body as any)}
            className={`${alignClass(col.align) || ''} ${col.className || ''}`.trim() || undefined}
            style={columnStyle(col)}
            headerStyle={col.headerStyle}
          />
        ))}
      </Table>
      </div>
    </div>
  );
}

export default SaasDataTable;
